import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Chip,
  Tooltip,
  InputAdornment,
  FormHelperText,
  Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { supabase } from '../../services/supabase';
import type { Game, League, Player, WinnerType } from '../../types/models';
import { apiCache } from '../../services/apiCache';
import { useAdminRole } from '../../contexts/AdminRoleContext';
import { fixTypes } from '../../services/adminPermissions';

// Cache keys and TTL
const GAMES_CACHE_KEY = 'admin_games_list';
const LEAGUES_CACHE_KEY = 'admin_leagues_list';
const JUDGES_CACHE_KEY = 'admin_judges_list';
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

interface GamesAdminProps {
  onSelectGameResults?: (gameId: string) => void;
  selectedLeagueId?: string | null;
  onSelectLeague?: (leagueId: string) => void;
}

const GamesAdmin: React.FC<GamesAdminProps> = ({ onSelectGameResults, selectedLeagueId, onSelectLeague }) => {
  const { userRole, isSuperAdmin, loading: roleLoading } = useAdminRole();
  const [games, setGames] = useState<Game[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [judges, setJudges] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  
  // Multi-select and delete functionality
  const [selectedGames, setSelectedGames] = useState<Set<string>>(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState<string | null>(null);
  
  // Form state
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [sessionNum, setSessionNum] = useState<number>(1);
  const [numberInSession, setNumberInSession] = useState<number>(1);
  const [leagueId, setLeagueId] = useState<string>('');
  const [occurredAt, setOccurredAt] = useState<string>(new Date().toISOString().substring(0, 16));
  const [winner, setWinner] = useState<WinnerType>('CIVILIANS');
  const [judgeId, setJudgeId] = useState<string>('');
  const [videoLink, setVideoLink] = useState<string>('');
  
  // Validation
  const [formErrors, setFormErrors] = useState({
    sessionNum: false,
    numberInSession: false,
    leagueId: false,
    judgeId: false
  });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Fetch data when role information is available
  useEffect(() => {
    if (!roleLoading) {
      // Load all data in parallel
      Promise.all([
        fetchLeagues(),
        fetchJudges(),
        fetchGames()
      ]);
    }
  }, [roleLoading, userRole?.id]);
  
  // Set initial leagueId based on prop
  useEffect(() => {
    if (leagues.length > 0) {
      if (selectedLeagueId) {
        // Verify that the league exists
        if (leagues.some(league => league.id === selectedLeagueId)) {
          setLeagueId(selectedLeagueId);
        } else {
          // If not, select the first league
          setLeagueId(leagues[0].id);
          if (onSelectLeague) {
            onSelectLeague(leagues[0].id);
          }
        }
      } else {
        // No league selected, set to first league
        setLeagueId(leagues[0].id);
        if (onSelectLeague) {
          onSelectLeague(leagues[0].id);
        }
      }
    }
  }, [leagues, selectedLeagueId, onSelectLeague]);

  // Filter games by league and search query
  useEffect(() => {
    // First filter by league
    let filtered = games;
    
    if (leagueId) {
      filtered = games.filter(game => game.league_id === leagueId);
    }
    
    // Then filter by search query if provided
    if (searchQuery) {
      const lowercaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(game => {
        // Search by session number, league name, or judge name
        return (
          `${game.session_num}-${game.number_in_session}`.includes(lowercaseQuery) ||
          (game.league as any)?.name?.toLowerCase().includes(lowercaseQuery) ||
          (game.judge as any)?.nickname?.toLowerCase().includes(lowercaseQuery)
        );
      });
    }
    
    setFilteredGames(filtered);
  }, [searchQuery, games, leagueId]);

  const getCacheKey = (baseKey: string) => {
    // Create user-specific cache keys
    return userRole 
      ? `${baseKey}_${userRole.id}_${userRole.role}`
      : baseKey;
  };

  const clearCaches = () => {
    // Clear all related caches when data changes
    apiCache.remove(getCacheKey(GAMES_CACHE_KEY));
    apiCache.remove(getCacheKey(LEAGUES_CACHE_KEY));
    apiCache.remove(getCacheKey(JUDGES_CACHE_KEY));
  };

  const fetchGames = async () => {
    setLoading(true);
    try {
      // Get from cache or fetch
      const gamesData = await apiCache.getOrFetch<Game[]>(
        getCacheKey(GAMES_CACHE_KEY),
        async () => {
          
          let queryResult;
          
          if (isSuperAdmin) {
            // Superadmins see all games
            queryResult = await supabase
              .from('games')
              .select(`
                *,
                league:league_id (
                  id,
                  name
                ),
                judge:judge_id (
                  id,
                  nickname,
                  full_name
                )
              `)
              .order('session_num', { ascending: false })
              .order('number_in_session', { ascending: false });
          } else if (userRole?.club_id) {
            // Regular admins only see games from their club's leagues
            // First get leagues from the admin's club
            const { data: leagues } = await supabase
              .from('leagues')
              .select('id')
              .eq('club_id', userRole.club_id);
              
            const leagueIds = leagues?.map(league => league.id) || [];
            
            if (leagueIds.length === 0) {
              // No leagues in this club, return empty result
              return [];
            } else {
              // Get games for these leagues
              queryResult = await supabase
                .from('games')
                .select(`
                  *,
                  league:league_id (
                    id,
                    name
                  ),
                  judge:judge_id (
                    id,
                    nickname,
                    full_name
                  )
                `)
                .in('league_id', leagueIds)
                .order('session_num', { ascending: false })
                .order('number_in_session', { ascending: false });
            }
          } else {
            // No club assigned
            return [];
          }
          
          const { data, error } = queryResult;
          
          if (error) {
            throw error;
          }
    
          // Use the fixTypes helper to ensure correct typing
          return fixTypes<Game>(data || []);
        },
        CACHE_TTL
      );
      
      setGames(gamesData);
      setFilteredGames(gamesData);
    } catch (error: any) {
      console.error('Error fetching games:', error.message);
      showSnackbar('Failed to load games', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeagues = async () => {
    try {
      // Get from cache or fetch from API
      const leaguesData = await apiCache.getOrFetch<League[]>(
        getCacheKey(LEAGUES_CACHE_KEY),
        async () => {
          
          let queryResult;
          
          if (isSuperAdmin) {
            // Superadmins see all leagues
            queryResult = await supabase
              .from('leagues')
              .select(`
                *,
                clubs (
                  id,
                  name
                )
              `)
              .order('name', { ascending: true });
          } else if (userRole?.club_id) {
            // Regular admins only see leagues from their club
            queryResult = await supabase
              .from('leagues')
              .select(`
                *,
                clubs (
                  id,
                  name
                )
              `)
              .eq('club_id', userRole.club_id)
              .order('name', { ascending: true });
          } else {
            // No club assigned, return empty result
            return [];
          }
    
          const { data, error } = queryResult;
          
          if (error) {
            throw error;
          }
    
          // Fix types with the helper function
          return fixTypes<League>(data || []);
        },
        CACHE_TTL
      );
      
      setLeagues(leaguesData);
    } catch (error: any) {
      console.error('Error fetching leagues:', error.message);
      showSnackbar('Failed to load leagues', 'error');
    }
  };

  const fetchJudges = async () => {
    try {
      // Get from cache or fetch from API
      const judgesData = await apiCache.getOrFetch<Player[]>(
        getCacheKey(JUDGES_CACHE_KEY),
        async () => {
          
          // Players don't have club_id, so no need for admin filtering
          const { data, error } = await supabase
            .from('players')
            .select('*')
            .eq('is_judge', true)
            .order('nickname', { ascending: true });
    
          if (error) {
            throw error;
          }
          
          // Use fixTypes to ensure proper typing
          return fixTypes<Player>(data || []);
        },
        CACHE_TTL
      );
      
      setJudges(judgesData);
    } catch (error: any) {
      console.error('Error fetching judges:', error.message);
      showSnackbar('Failed to load judges', 'error');
    }
  };

  const handleOpenDialog = (game?: Game) => {
    if (game) {
      setEditingGameId(game.id);
      setSessionNum(game.session_num);
      setNumberInSession(game.number_in_session);
      setLeagueId(game.league_id);
      setOccurredAt(new Date(game.occurred_at).toISOString().substring(0, 16));
      setWinner(game.winner as WinnerType);
      setJudgeId(game.judge_id);
      setVideoLink(game.video_link || '');
    } else {
      resetForm();
      
      // Set default values for new game
      if (leagues.length > 0) {
        setLeagueId(leagues[0].id);
      }
      
      if (judges.length > 0) {
        setJudgeId(judges[0].id);
      }
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const resetForm = () => {
    setEditingGameId(null);
    setSessionNum(1);
    setNumberInSession(1);
    // Don't reset leagueId here to keep the current filter
    setOccurredAt(new Date().toISOString().substring(0, 16));
    setWinner('CIVILIANS');
    setJudgeId('');
    setVideoLink('');
    setFormErrors({
      sessionNum: false,
      numberInSession: false,
      leagueId: false,
      judgeId: false
    });
  };

  const validateForm = (): boolean => {
    const errors = {
      sessionNum: sessionNum < 1,
      numberInSession: numberInSession < 1,
      leagueId: !leagueId,
      judgeId: !judgeId
    };
    
    setFormErrors(errors);
    
    return !Object.values(errors).some(Boolean);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLeagueChange = (event: SelectChangeEvent) => {
    const newLeagueId = event.target.value;
    setLeagueId(newLeagueId);
    // Notify parent component about the league change
    if (onSelectLeague) {
      onSelectLeague(newLeagueId);
    }
  };

  const handleJudgeChange = (event: SelectChangeEvent) => {
    setJudgeId(event.target.value);
  };

  const handleWinnerChange = (event: SelectChangeEvent) => {
    setWinner(event.target.value as WinnerType);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const gameData = {
        session_num: sessionNum,
        number_in_session: numberInSession,
        league_id: leagueId,
        occurred_at: new Date(occurredAt).toISOString(),
        winner,
        judge_id: judgeId,
        video_link: videoLink.trim() || null
      };

      if (editingGameId) {
        // Track the old winner if we're editing
        let oldWinner: WinnerType | null = null;
        
        // Get the current winner of the game before updating
        if (games.length > 0) {
          const gameToEdit = games.find(g => g.id === editingGameId);
          if (gameToEdit) {
            oldWinner = gameToEdit.winner as WinnerType;
          }
        }
        
        // Update existing game
        const { error } = await supabase
          .from('games')
          .update(gameData)
          .eq('id', editingGameId);

        if (error) throw error;
        
        // If the winner changed, update all game results for this game
        if (oldWinner !== null && oldWinner !== winner) {
          // Fetch all game results for this game
          const { data: gameResults, error: resultsError } = await supabase
            .from('game_results')
            .select('*')
            .eq('game_id', editingGameId);
            
          if (resultsError) throw resultsError;
          
          if (gameResults && gameResults.length > 0) {
            // Update each result with the correct is_win value based on the new winner
            const updatePromises = gameResults.map(result => {
              // For a TIE, all players lose, for other winners, citizen/sheriff win if civilians win, mafia/don win if mafia wins
              const newIsWin = winner === 'TIE' 
                ? false 
                : winner === 'CIVILIANS'
                  ? (result.role === 'CITIZEN' || result.role === 'SHERIFF') 
                  : (result.role === 'MAFIA' || result.role === 'DON');
                
              // Only update if the win status actually changed
              if (result.is_win !== newIsWin) {
                return supabase
                  .from('game_results')
                  .update({ is_win: newIsWin })
                  .eq('id', result.id);
              }
              
              return Promise.resolve({ data: null, error: null });
            });
            
            // Wait for all updates to complete
            await Promise.all(updatePromises);
          }
        }
        
        showSnackbar('Game updated successfully', 'success');
      } else {
        // Create new game
        const { error } = await supabase
          .from('games')
          .insert([gameData]);

        if (error) throw error;
        showSnackbar('Game created successfully', 'success');
      }

      // Clear cache after modifications
      clearCaches();
      
      handleCloseDialog();
      fetchGames();
    } catch (error: any) {
      console.error('Error saving game:', error.message);
      showSnackbar(`Failed to ${editingGameId ? 'update' : 'create'} game`, 'error');
    }
  };

  const handleToggleSelectGame = (gameId: string) => {
    const newSelectedGames = new Set(selectedGames);
    if (newSelectedGames.has(gameId)) {
      newSelectedGames.delete(gameId);
    } else {
      newSelectedGames.add(gameId);
    }
    setSelectedGames(newSelectedGames);
  };

  const handleSelectAllGames = () => {
    if (selectedGames.size === filteredGames.length) {
      // If all are selected, deselect all
      setSelectedGames(new Set());
    } else {
      // Otherwise select all filtered games
      const allIds = filteredGames.map(game => game.id);
      setSelectedGames(new Set(allIds));
    }
  };

  const openDeleteConfirmation = (id: string | null = null) => {
    setGameToDelete(id);
    setConfirmDeleteOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setConfirmDeleteOpen(false);
    setGameToDelete(null);
  };

  const handleDelete = async () => {
    try {
      if (gameToDelete) {
        // Delete single game
        const { error } = await supabase
          .from('games')
          .delete()
          .eq('id', gameToDelete);

        if (error) throw error;
        
        showSnackbar('Game deleted successfully', 'success');
      } else if (selectedGames.size > 0) {
        // Delete multiple games
        const selectedIds = Array.from(selectedGames);
        const { error } = await supabase
          .from('games')
          .delete()
          .in('id', selectedIds);

        if (error) throw error;
        
        showSnackbar(`${selectedGames.size} games deleted successfully`, 'success');
        setSelectedGames(new Set());
      }
      
      // Clear cache after modifications
      clearCaches();
      
      fetchGames();
      closeDeleteConfirmation();
    } catch (error: any) {
      console.error('Error deleting game(s):', error.message);
      showSnackbar('Failed to delete game(s)', 'error');
      closeDeleteConfirmation();
    }
  };

  const getWinnerChipColor = (winner: WinnerType) => {
    switch (winner) {
      case 'CIVILIANS':
        return 'primary';
      case 'MAFIA':
        return 'error';
      case 'TIE':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch (error) {
      return dateString;
    }
  };

  // Show loading if we're waiting for role data or initial data fetch
  if (roleLoading || (loading && games.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" component="h2" sx={{ mr: 2 }}>
            Manage Games
          </Typography>
          {selectedGames.size > 0 && (
            <Chip 
              label={`${selectedGames.size} selected`} 
              color="primary" 
              variant="outlined"
              onDelete={() => setSelectedGames(new Set())}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedGames.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => openDeleteConfirmation()}
              sx={{ borderRadius: 2 }}
            >
              Delete Selected
            </Button>
          )}
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ borderRadius: 2 }}
            disabled={leagues.length === 0 || judges.length === 0}
          >
            Add Game
          </Button>
        </Box>
      </Box>

      {(leagues.length === 0 || judges.length === 0) && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
          <Typography color="warning.main">
            {leagues.length === 0 && judges.length === 0 && 'You need to create at least one league and one judge before you can add games.'}
            {leagues.length === 0 && judges.length > 0 && 'You need to create at least one league before you can add games.'}
            {leagues.length > 0 && judges.length === 0 && 'You need to create at least one judge (player with judge status) before you can add games.'}
          </Typography>
        </Paper>
      )}
      
      {leagues.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Select League
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }}>
            <InputLabel id="league-filter-label">League</InputLabel>
            <Select
              labelId="league-filter-label"
              id="league-filter"
              value={leagueId}
              onChange={handleLeagueChange}
              label="League"
            >
              {leagues.map((league) => {
                const clubName = (league.clubs as any)?.name || '';
                const displayName = clubName ? `${league.name} (${clubName})` : league.name;
                
                return (
                  <MenuItem key={league.id} value={league.id}>
                    {displayName}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText>Games are filtered by the selected league</FormHelperText>
          </FormControl>
        </Paper>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search games by session, league, or judge..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredGames.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography>
            {searchQuery ? 'No games match your search.' : 'No games found. Create your first game!'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedGames.size > 0 && selectedGames.size < filteredGames.length}
                    checked={filteredGames.length > 0 && selectedGames.size === filteredGames.length}
                    onChange={handleSelectAllGames}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Game</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>League</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Winner</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Judge</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGames.map((game) => {
                const league = (game.league as any)?.name || 'Unknown League';
                const judge = (game.judge as any)?.nickname || 'Unknown Judge';
                
                return (
                  <TableRow 
                    key={game.id} 
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      bgcolor: selectedGames.has(game.id) ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleToggleSelectGame(game.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedGames.has(game.id)}
                        onChange={(e) => {
                          e.stopPropagation(); // Prevent row click handler
                          handleToggleSelectGame(game.id);
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent row click handler
                      />
                    </TableCell>
                    <TableCell component="th" scope="row">
                      Session {game.session_num}, Game {game.number_in_session}
                    </TableCell>
                    <TableCell>{league}</TableCell>
                    <TableCell>{formatDate(game.occurred_at)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={game.winner} 
                        color={getWinnerChipColor(game.winner as WinnerType)}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                        onClick={(e) => e.stopPropagation()} // Prevent row click handler
                      />
                    </TableCell>
                    <TableCell>{judge}</TableCell>
                    <TableCell align="right">
                      {onSelectGameResults && (
                        <Tooltip title="View game results">
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click handler
                              onSelectGameResults(game.id);
                            }} 
                            color="info" 
                            size="small"
                          >
                            <ViewListIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit game">
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click handler
                            handleOpenDialog(game);
                          }} 
                          color="primary" 
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete game">
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click handler
                            openDeleteConfirmation(game.id);
                          }} 
                          color="error" 
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog for adding/editing game */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingGameId ? 'Edit Game' : 'Add New Game'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                margin="dense"
                id="sessionNum"
                label="Session Number"
                type="number"
                fullWidth
                variant="outlined"
                value={sessionNum}
                onChange={(e) => setSessionNum(Number(e.target.value))}
                InputProps={{ inputProps: { min: 1 } }}
                error={formErrors.sessionNum}
                helperText={formErrors.sessionNum ? "Required, must be ≥ 1" : ""}
              />
              <TextField
                margin="dense"
                id="numberInSession"
                label="Game Number in Session"
                type="number"
                fullWidth
                variant="outlined"
                value={numberInSession}
                onChange={(e) => setNumberInSession(Number(e.target.value))}
                InputProps={{ inputProps: { min: 1 } }}
                error={formErrors.numberInSession}
                helperText={formErrors.numberInSession ? "Required, must be ≥ 1" : ""}
              />
            </Stack>
            
            <TextField
              label="Date & Time"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            <FormControl fullWidth variant="outlined" error={formErrors.leagueId}>
              <InputLabel id="league-select-label">League</InputLabel>
              <Select
                labelId="league-select-label"
                id="league-select"
                value={leagueId}
                onChange={handleLeagueChange}
                label="League"
              >
                {leagues.map((league) => {
                  const clubName = (league.clubs as any)?.name || '';
                  const displayName = clubName ? `${league.name} (${clubName})` : league.name;
                  
                  return (
                    <MenuItem key={league.id} value={league.id}>
                      {displayName}
                    </MenuItem>
                  );
                })}
              </Select>
              {formErrors.leagueId && <FormHelperText>Please select a league</FormHelperText>}
            </FormControl>
            
            <FormControl fullWidth variant="outlined" error={formErrors.judgeId}>
              <InputLabel id="judge-select-label">Judge</InputLabel>
              <Select
                labelId="judge-select-label"
                id="judge-select"
                value={judgeId}
                onChange={handleJudgeChange}
                label="Judge"
              >
                {judges.map((judge) => (
                  <MenuItem key={judge.id} value={judge.id}>
                    {judge.nickname} ({judge.full_name})
                  </MenuItem>
                ))}
              </Select>
              {formErrors.judgeId && <FormHelperText>Please select a judge</FormHelperText>}
            </FormControl>
            
            <FormControl fullWidth variant="outlined">
              <InputLabel id="winner-select-label">Winner</InputLabel>
              <Select
                labelId="winner-select-label"
                id="winner-select"
                value={winner}
                onChange={handleWinnerChange}
                label="Winner"
              >
                <MenuItem value="CIVILIANS">Civilians</MenuItem>
                <MenuItem value="MAFIA">Mafia</MenuItem>
                <MenuItem value="TIE">Tie</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              margin="dense"
              id="videoLink"
              label="Video Link (Optional)"
              type="url"
              fullWidth
              variant="outlined"
              value={videoLink}
              onChange={(e) => setVideoLink(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              helperText="Link to YouTube or other video platform where this game can be viewed"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="primary" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="secondary"
            sx={{ borderRadius: 2 }}
          >
            {editingGameId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDeleteOpen} onClose={closeDeleteConfirmation}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {gameToDelete ? 
              'Are you sure you want to delete this game? This will also delete all associated game results.' :
              `Are you sure you want to delete ${selectedGames.size} selected games? This will also delete all associated game results.`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmation} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GamesAdmin;